import { ApiError, isRetryable } from './errors.ts';
import type { Problem } from './types.ts';

/** Project schemaVersion this client understands (sent as X-Client-Schema on sync routes). */
export const CLIENT_SCHEMA = 1;

export type TokenProvider = (opts?: { forceRefresh?: boolean }) => Promise<string | null>;

export interface RequestOptions {
  query?: Record<string, string | number | undefined>;
  body?: unknown;
  headers?: Record<string, string>;
  /** Sends `Idempotency-Key`; the same key is reused for every retry. `true` = generate one. */
  idempotencyKey?: string | true;
  /** Retries on network errors, 429 and 502–504 (default: GET/DELETE and idempotent writes). */
  retry?: boolean;
  signal?: AbortSignal;
  /** Sends X-Client-Schema. */
  clientSchema?: boolean;
  auth?: boolean;
}

const BACKOFF_MS = [400, 1500, 4000];

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    });
  });

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * Small typed fetch wrapper for the Better Lights API: bearer auth (refreshes once on 401),
 * RFC 9457 problem+json errors as `ApiError`, retries with backoff that reuse one
 * Idempotency-Key, and X-Client-Schema on sync routes.
 */
export class ApiClient {
  constructor(
    readonly baseUrl: string,
    private readonly token: TokenProvider,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  url(path: string, query?: RequestOptions['query']): string {
    const q = query
      ? Object.entries(query)
          .filter(([, v]) => v !== undefined && v !== '')
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    return `${this.baseUrl}${path}${q ? `?${q}` : ''}`;
  }

  async headers(opts: RequestOptions, forceRefresh = false): Promise<Record<string, string>> {
    const h: Record<string, string> = {
      Accept: 'application/json, application/problem+json',
      'X-Client-Platform': 'web',
      ...opts.headers,
    };
    if (opts.body !== undefined) h['Content-Type'] = 'application/json';
    if (opts.clientSchema) h['X-Client-Schema'] = String(CLIENT_SCHEMA);
    if (opts.auth !== false) {
      const t = await this.token({ forceRefresh });
      if (t) h.Authorization = `Bearer ${t}`;
    }
    return h;
  }

  async request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const key = opts.idempotencyKey === true ? newIdempotencyKey() : opts.idempotencyKey;
    const retry = opts.retry ?? (method === 'GET' || method === 'DELETE' || key !== undefined);
    let refreshed = false;
    for (let attempt = 0; ; attempt++) {
      try {
        const headers = await this.headers(opts, refreshed);
        if (key) headers['Idempotency-Key'] = key;
        return await this.once<T>(method, this.url(path, opts.query), headers, opts);
      } catch (e) {
        if (e instanceof ApiError && e.status === 401 && !refreshed && opts.auth !== false) {
          refreshed = true;
          continue;
        }
        if (!retry || !isRetryable(e) || attempt >= BACKOFF_MS.length || opts.signal?.aborted) throw e;
        const wait =
          e instanceof ApiError && e.retryAfter ? Math.min(30_000, e.retryAfter * 1000) : BACKOFF_MS[attempt];
        await sleep(wait * (0.8 + Math.random() * 0.4), opts.signal);
      }
    }
  }

  private async once<T>(
    method: string,
    url: string,
    headers: Record<string, string>,
    opts: RequestOptions,
  ): Promise<T> {
    let res: Response;
    try {
      res = await this.fetchImpl(url, {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        signal: opts.signal,
      });
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') throw e;
      throw new ApiError(0, null, 'Network error');
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let json: unknown = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }
    if (!res.ok) {
      const problem = json && typeof json === 'object' && 'code' in json ? (json as Problem) : null;
      const retryAfter = Number(res.headers.get('Retry-After'));
      const p = problem ?? null;
      if (p && !p.retryAfter && Number.isFinite(retryAfter) && retryAfter > 0) p.retryAfter = retryAfter;
      throw new ApiError(res.status, p, `Request failed (HTTP ${res.status}).`);
    }
    return json as T;
  }
}
